import { z } from 'zod';
import { TokenPair } from '../client/auth-store';
/**
 * The app's way of opening a web page already signed in.
 *
 * Billing is managed on the web, and the phone's browser has no session of its
 * own. The app mints a one-time code, opens `/account/handoff#code=…`, and the
 * web trades it for a session. Single use, 60 seconds, and bound by the server
 * to the page it was minted for — see the backend's `SessionHandoff`.
 *
 * ── The fragment, not the query ───────────────────────────────────────────
 * `#code=` never leaves the browser: it is in no request line, so in no access
 * log, no `Referer` and no CDN cache key. A `?code=` would be in all four.
 */
export const HANDOFF_PATH = '/account/handoff';
export const SessionHandoffCode = z.object({ code: z.string(), expiresAt: z.string() });
export const RedeemedSessionHandoff = TokenPair.extend({ path: z.string() });
/** The app's half. `http` is the signed-in client. */
export function createSessionHandoff(http, path) {
    return http.post('/auth/handoff', { path }, { schema: SessionHandoffCode });
}
/** The web's half. `http` must be the PUBLIC client: nobody is signed in yet. */
export function redeemSessionHandoff(http, code) {
    return http.post('/auth/handoff/redeem', { code }, { schema: RedeemedSessionHandoff });
}
/** `…/account/handoff#code=…`, given the page's own URL without a fragment. */
export function handoffUrl(handoffPageUrl, code) {
    return `${handoffPageUrl}#code=${encodeURIComponent(code)}`;
}
/** The code out of `location.hash`, or `null`. */
export function handoffCodeFrom(hash) {
    const code = new URLSearchParams(hash.replace(/^#/, '')).get('code');
    return code && code.length >= 20 ? code : null;
}
/**
 * Whether the web may navigate to what the server answered.
 *
 * The server already bound the path to the code and validated it against its
 * own allowlist; this is the second lock on the same door, so a bug on either
 * side alone cannot turn the handoff into an open redirect.
 */
export function isSafeHandoffTarget(path) {
    return (/^\/[A-Za-z0-9\-._~/]*$/.test(path) &&
        !path.includes('//') &&
        !path.includes('..') &&
        (path === '/billing' || path.startsWith('/billing/')));
}
//# sourceMappingURL=session-handoff.js.map